import { useRef } from "react";
import { ChevronLeft, ChevronRight, ChevronUp, Footprints, Hand } from "lucide-react";

const RANGE = 150;
const R = 54, CIRC = 2 * Math.PI * R;

/** Drag-to-move pad: vertical drag = knee/foot lift, outward drag = weight shift. Amount 0..1 of the patient's safe range. */
export const FootPad = ({ side, active, axis, amount, target, onDrag, onRelease, limb = "leg" }) => {
  const Icon = limb === "arm" ? Hand : Footprints, label = limb === "arm" ? `Main ${side === "left" ? "gauche" : "droite"}` : `Pied ${side === "left" ? "gauche" : "droit"}`;
  const st = useRef(null);
  const down = (e) => { if (!active) return; e.currentTarget.setPointerCapture(e.pointerId); st.current = { x: e.clientX, y: e.clientY, a: 0, t: performance.now() }; onDrag(0, 0); };
  const move = (e) => {
    if (!st.current) return;
    const d = axis === "x" ? (side === "left" ? st.current.x - e.clientX : e.clientX - st.current.x) : st.current.y - e.clientY;
    const a = Math.max(0, Math.min(1, d / RANGE)), now = performance.now(), dt = Math.max(1, now - st.current.t) / 1000;
    const speed = Math.abs(a - st.current.a) / dt;
    st.current = { ...st.current, a, t: now };
    onDrag(a, speed);
  };
  const up = () => { if (!st.current) return; st.current = null; onRelease(); };
  const Arrow = axis === "x" ? (side === "left" ? ChevronLeft : ChevronRight) : ChevronUp;
  const tAng = target * 2 * Math.PI - Math.PI / 2;
  return (
    <button data-testid={`foot-pad-${side}`} aria-label={label} data-limb={limb} aria-disabled={!active}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      className={`relative flex h-[118px] w-[118px] shrink-0 touch-none select-none flex-col items-center justify-center rounded-full border-4 transition-[opacity,transform,box-shadow] duration-300 ${active ? "border-[#4FD1C5] bg-[#0A2540] shadow-[0_0_28px_6px_rgba(79,209,197,.55)]" : "border-white/40 bg-[#0A2540]/70 opacity-60"}`}>
      <svg viewBox="0 0 120 120" className="absolute inset-0 -rotate-90" aria-hidden>
        <circle cx="60" cy="60" r={R} stroke="#ffffff22" strokeWidth="7" fill="none" />
        {active && <circle cx="60" cy="60" r={R} stroke={amount >= target ? "#E0B33A" : "#4FD1C5"} strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={`${amount * CIRC} ${CIRC}`} />}
      </svg>
      {active && <span className="absolute h-3.5 w-3.5 rounded-full border-2 border-white bg-[#E0B33A]" style={{ left: 59 + Math.cos(tAng) * R - 7, top: 59 + Math.sin(tAng) * R - 7 }} />}
      <Icon className={`h-9 w-9 text-white ${side === "left" ? "-scale-x-100" : ""}`} />
      <span className="mt-1 text-xs font-extrabold text-white">{label}</span>
      {active && amount === 0 && <Arrow className="pad-hint absolute -top-1 h-6 w-6 text-[#4FD1C5]" />}
      <span className="sr-only" data-testid={`foot-pad-${side}-amount`}>{Math.round(amount * 100)}</span>
    </button>
  );
};
